import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Heart, UserPlus, CheckCircle, XCircle, MapPin, Briefcase, GraduationCap, Loader2, Crown, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useTheme, themes } from './ThemeProvider';
import { toast } from 'sonner';
import { API_URL } from '../lib/api-client';

interface ReceivedInterest {
  _id: string;
  interestId: string;
  userId: string;
  name: string;
  age: number;
  photos: string[];
  location: string;
  profession: string;
  education: string;
  height: string;
  isPro?: boolean;
  isVerified?: boolean;
  sentAt: string;
  message?: string;
}

export function NewInterestModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [interests, setInterests] = useState<ReceivedInterest[]>([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState<string | null>(null);
  const [respondedInterests, setRespondedInterests] = useState<Set<string>>(new Set());
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  // Check and show modal based on frequency
  useEffect(() => {
    checkAndShowModal();
  }, []);

  const checkAndShowModal = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if user has permanently disabled this modal
      const userDisabled = localStorage.getItem('newInterestModalDisabled');
      if (userDisabled === 'true') return;

      const lastShownDate = localStorage.getItem('newInterestModalLastShown');
      const today = new Date();
      
      // Fetch modal config from backend
      const axios = (await import('axios')).default;
      const configResponse = await axios.get(`${API_URL}/new-interest-modal-config/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const config = configResponse.data?.data || {};
      const isEnabled = config.enabled !== false;
      const frequencyDays = config.frequencyDays || 2;
      const showDelay = config.showDelay || 3000;

      if (!isEnabled) return;

      // Check if enough days have passed
      if (lastShownDate) {
        const lastShown = new Date(lastShownDate);
        const daysSinceLastShown = Math.floor((today.getTime() - lastShown.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysSinceLastShown < frequencyDays) {
          return;
        }
      }

      // Fetch new interests
      const interestsAvailable = await fetchNewInterests();
      
      // Only update last shown if we have interests to show
      if (interestsAvailable) {
        localStorage.setItem('newInterestModalLastShown', today.toISOString());
        
        // Show modal after delay
        setTimeout(() => {
          setIsOpen(true);
        }, showDelay);
      }
      
    } catch (error) {
      // Silently fail - modal is optional
    }
  };

  const fetchNewInterests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return false;

      const axios = (await import('axios')).default;
      const response = await axios.get(`${API_URL}/interactions/new-interests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 10 }
      });

      if (response.data.success) {
        const interestsList = response.data.data.interests || [];
        setInterests(interestsList);
        
        // Don't show modal if no interests
        if (interestsList.length === 0) {
          setIsOpen(false);
          return false;
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInterest = async (interestId: string) => {
    if (responding || respondedInterests.has(interestId)) return;

    setResponding(interestId);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to respond');
        return;
      }

      const axios = (await import('axios')).default;
      await axios.post(
        `${API_URL}/interactions/accept/${interestId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Mark as responded
      setRespondedInterests(prev => new Set([...prev, interestId]));
      toast.success('Interest accepted! You can now connect.');
      
      // Remove from list
      setInterests(prev => prev.filter(i => i.interestId !== interestId));
      
      // Close modal if no more interests
      if (interests.length <= 1) {
        setTimeout(() => setIsOpen(false), 1000);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to accept interest');
    } finally {
      setResponding(null);
    }
  };

  const handleDeclineInterest = async (interestId: string) => {
    if (responding || respondedInterests.has(interestId)) return;

    setResponding(interestId);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to respond');
        return;
      }

      const axios = (await import('axios')).default;
      await axios.post(
        `${API_URL}/interactions/decline/${interestId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Mark as responded
      setRespondedInterests(prev => new Set([...prev, interestId]));
      toast.success('Interest declined');
      
      // Remove from list
      setInterests(prev => prev.filter(i => i.interestId !== interestId));
      
      // Close modal if no more interests
      if (interests.length <= 1) {
        setTimeout(() => setIsOpen(false), 1000);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to decline interest');
    } finally {
      setResponding(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('newInterestModalDisabled', 'true');
    toast.info('You won\'t see this modal again. Check Activity tab for new interests.');
    setIsOpen(false);
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const sent = new Date(date);
    const diffMs = now.getTime() - sent.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`${currentTheme.gradient} p-6 text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Heart className="size-8 fill-current" />
                    </motion.div>
                    <div>
                      <h2 className="text-2xl font-bold">New Interests Received!</h2>
                      <p className="text-white/90 text-sm">People are interested in connecting with you</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="size-6" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                    <UserPlus className="size-3 mr-1" />
                    {interests.length} New Interest{interests.length !== 1 ? 's' : ''}
                  </Badge>
                  <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                    <Heart className="size-3 mr-1" />
                    Respond Now
                  </Badge>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="size-12 animate-spin text-rose-500 mx-auto mb-4" />
                    <p className="text-gray-600">Loading new interests...</p>
                  </div>
                </div>
              ) : interests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="size-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                  <p className="text-gray-600">You've responded to all interests</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {interests.map((interest, index) => {
                    const hasResponded = respondedInterests.has(interest.interestId);
                    
                    return (
                      <motion.div
                        key={interest.interestId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                      >
                        <Card className="overflow-hidden border-2 border-rose-100 shadow-lg hover:shadow-2xl transition-all">
                          {/* Profile Image */}
                          <div className="relative h-52">
                            <ImageWithFallback
                              src={interest.photos[0] || '/placeholder.jpg'}
                              alt={interest.name}
                              className="w-full h-full object-cover"
                            />
                            
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                            
                            {/* Badges */}
                            <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-1">
                              <Badge className="bg-rose-500 border-0 text-white text-xs backdrop-blur-sm animate-pulse">
                                <Heart className="size-3 mr-1" />
                                New
                              </Badge>
                              {interest.isVerified && (
                                <Badge className="bg-blue-500 border-0 text-white text-xs backdrop-blur-sm">
                                  <CheckCircle className="size-3 mr-1" />
                                  Verified
                                </Badge>
                              )}
                              {interest.isPro && (
                                <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 border-0 text-white text-xs ml-auto">
                                  <Crown className="size-3 mr-1" />
                                  Pro
                                </Badge>
                              )}
                            </div>

                            {/* Time Ago */}
                            <div className="absolute top-2 right-2">
                              <Badge className="bg-black/40 text-white text-xs backdrop-blur-sm border-0">
                                <Clock className="size-3 mr-1" />
                                {getTimeAgo(interest.sentAt)}
                              </Badge>
                            </div>

                            {/* Name & Age */}
                            <div className="absolute bottom-2 left-2 right-2 text-white">
                              <h3 className="font-bold text-lg truncate">
                                {interest.name}, {interest.age}
                              </h3>
                              <p className="text-xs text-white/90 flex items-center gap-1 truncate">
                                <MapPin className="size-3 flex-shrink-0" />
                                {interest.location}
                              </p>
                            </div>
                          </div>

                          {/* Profile Details */}
                          <div className="p-4 space-y-3">
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <Briefcase className="size-3 flex-shrink-0 text-blue-500" />
                              <span className="truncate">{interest.profession}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <GraduationCap className="size-3 flex-shrink-0 text-purple-500" />
                              <span className="truncate">{interest.education}</span>
                            </div>

                            {interest.message && (
                              <div className="p-2 bg-rose-50 rounded text-xs text-gray-700 italic">
                                "{interest.message}"
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                              <Button
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => handleAcceptInterest(interest.interestId)}
                                disabled={responding === interest.interestId || hasResponded}
                              >
                                {responding === interest.interestId ? (
                                  <Loader2 className="size-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="size-4 mr-1" />
                                    Accept
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDeclineInterest(interest.interestId)}
                                disabled={responding === interest.interestId || hasResponded}
                              >
                                {responding === interest.interestId ? (
                                  <Loader2 className="size-4 animate-spin" />
                                ) : (
                                  <>
                                    <XCircle className="size-4 mr-1" />
                                    Decline
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-600">
                  <Heart className="size-4 inline mr-1 text-rose-500" />
                  {interests.length} {interests.length === 1 ? 'person is' : 'people are'} waiting for your response
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleDontShowAgain} className="text-gray-600 hover:text-gray-800">
                    Don't show again
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
